export type FormatAmountOptions = {
	decimals?: number;
	minDecimals?: number;
	usd?: boolean;
	compact?: boolean;
	fallback?: string;
};

export function formatAmount(
	value: number | null | undefined,
	options: FormatAmountOptions = {},
): string {
	const fallback = options.fallback ?? "—";
	if (value == null || !Number.isFinite(value)) return fallback;
	const decimals = options.decimals ?? 2;
	const minDecimals = options.minDecimals ?? Math.min(2, decimals);
	if (options.compact) {
		const v = Math.abs(value);
		const sign = value < 0 ? "-" : "";
		const prefix = options.usd ? "$" : "";
		if (v >= 1_000_000)
			return `${sign}${prefix}${(v / 1_000_000).toFixed(1)}M`;
		if (v >= 1_000) return `${sign}${prefix}${(v / 1_000).toFixed(1)}K`;
	}
	const abs = Math.abs(value);
	const sign = value < 0 ? "-" : "";
	const body = abs.toLocaleString("en-US", {
		minimumFractionDigits: minDecimals,
		maximumFractionDigits: decimals,
	});
	return options.usd ? `${sign}$${body}` : `${sign}${body}`;
}

export function amountSizeClass(
	formattedOrValue: string | number,
	base: "xs" | "sm" | "md" | "lg" | "xl" = "md",
): string {
	const text =
		typeof formattedOrValue === "number"
			? formatAmount(formattedOrValue)
			: formattedOrValue;
	const digits = text.replace(/[^0-9]/g, "").length;
	const scale: Record<typeof base, [string, string, string, string]> = {
		xs: ["text-xs", "text-[11px]", "text-[10px]", "text-[9px]"],
		sm: ["text-sm", "text-xs", "text-[11px]", "text-[10px]"],
		md: ["text-base", "text-sm", "text-xs", "text-[11px]"],
		lg: ["text-xl", "text-lg", "text-base", "text-sm"],
		xl: ["text-2xl", "text-xl", "text-lg", "text-base"],
	};
	const [a, b, c, d] = scale[base];
	if (digits >= 14) return d;
	if (digits >= 11) return c;
	if (digits >= 8) return b;
	return a;
}
